import React, { useState, useEffect, useCallback } from 'react';
import {
  BookOpen,
  Calendar,
  TrendingUp,
  Smile,
  Zap,
  Moon,
  Brain,
  Plus,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Loader2,
  Save,
} from 'lucide-react';
import { getUser } from '../../utils/helpers';
import { NODE_BACKEND_URL } from '../../utils/constant';

export default function SmartJournalPage() {
  const [activeTab, setActiveTab] = useState('calendar');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [moodRating, setMoodRating] = useState(5);
  const [energyLevel, setEnergyLevel] = useState(5);
  const [sleepQuality, setSleepQuality] = useState(5);
  const [journalNote, setJournalNote] = useState('');

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [todayEntry, setTodayEntry] = useState(null);

  const user = getUser();
  const userId = user?.id || user?._id || 'anonymous';

  const tabs = [
    { id: 'calendar', label: 'Check-in', icon: Calendar },
    { id: 'entries', label: 'Entries', icon: Edit3 },
    { id: 'insights', label: 'Insights', icon: TrendingUp },
  ];

  const moodEmojis = ['😢', '😔', '😕', '😐', '🙂', '😊', '😃', '😄', '🤩', '🥳'];
  const formatDateKey = (date) => date.toISOString().split('T')[0];
  const formatDisplayDate = (date) =>
    date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const formatEntryDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${NODE_BACKEND_URL}/api/strength/user/${userId}/journal`);
      if (!response.ok) throw new Error('Failed to load journal entries');
      const data = await response.json();
      const allEntries = data.result || [];
      setEntries(allEntries);
      const todayKey = formatDateKey(selectedDate);
      const existing = allEntries.find((e) => e.date === todayKey);
      if (existing) {
        setTodayEntry(existing);
        setMoodRating(existing.mood || 5);
        setEnergyLevel(existing.energy || 5);
        setSleepQuality(existing.sleep || 5);
        setJournalNote(existing.note || '');
      } else {
        setTodayEntry(null);
        setMoodRating(5);
        setEnergyLevel(5);
        setSleepQuality(5);
        setJournalNote('');
      }
    } catch (err) {
      console.error('[Journal] Load error:', err);
      setError('Failed to load journal entries');
    } finally {
      setLoading(false);
    }
  }, [userId, selectedDate]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  useEffect(() => {
    const dateKey = formatDateKey(selectedDate);
    const existing = entries.find((e) => e.date === dateKey);
    if (existing) {
      setTodayEntry(existing);
      setMoodRating(existing.mood || 5);
      setEnergyLevel(existing.energy || 5);
      setSleepQuality(existing.sleep || 5);
      setJournalNote(existing.note || '');
    } else {
      setTodayEntry(null);
      setMoodRating(5);
      setEnergyLevel(5);
      setSleepQuality(5);
      setJournalNote('');
    }
  }, [selectedDate, entries]);

  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  const handleSaveEntry = async () => {
    setSaving(true);
    setError(null);
    try {
      const entry = {
        date: formatDateKey(selectedDate),
        mood: moodRating,
        energy: energyLevel,
        sleep: sleepQuality,
        note: journalNote,
      };
      const response = await fetch(`${NODE_BACKEND_URL}/api/strength/user/${userId}/journal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      });
      if (!response.ok) throw new Error('Failed to save journal entry');
      const data = await response.json();
      const savedEntry = data.result;
      setTodayEntry(savedEntry);
      setEntries((prev) => {
        const filtered = prev.filter((e) => e.date !== savedEntry.date);
        return [savedEntry, ...filtered];
      });
    } catch (err) {
      console.error('[Journal] Save error:', err);
      setError('Failed to save entry. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const computeInsights = () => {
    if (entries.length < 2) return [];
    const insights = [];
    const recentEntries = entries.slice(0, 14);
    const highSleepEntries = recentEntries.filter((e) => (e.sleep || 0) >= 8);
    const lowSleepEntries = recentEntries.filter((e) => (e.sleep || 0) < 6);
    if (highSleepEntries.length > 0 && lowSleepEntries.length > 0) {
      const highSleepMood = highSleepEntries.reduce((s, e) => s + (e.mood || 0), 0) / highSleepEntries.length;
      const lowSleepMood = lowSleepEntries.reduce((s, e) => s + (e.mood || 0), 0) / lowSleepEntries.length;
      const diff = ((highSleepMood - lowSleepMood) / lowSleepMood * 100).toFixed(0);
      if (diff > 0) {
        insights.push({ title: 'Sleep Quality Impact', description: `Mood is ${diff}% higher on 8+ hour nights.`, trend: 'up', priority: 'high' });
      }
    }
    if (recentEntries.length >= 4) {
      const half = Math.floor(recentEntries.length / 2);
      const recentAvg = recentEntries.slice(0, half).reduce((s, e) => s + (e.energy || 0), 0) / half;
      const olderAvg = recentEntries.slice(half).reduce((s, e) => s + (e.energy || 0), 0) / (recentEntries.length - half);
      insights.push({
        title: 'Energy Trend',
        description: recentAvg > olderAvg
          ? `Energy trending up (${olderAvg.toFixed(1)} → ${recentAvg.toFixed(1)}). Keep it up!`
          : recentAvg < olderAvg
          ? `Energy dipped recently (${olderAvg.toFixed(1)} → ${recentAvg.toFixed(1)}). Adjust recovery.`
          : 'Energy levels are stable.',
        trend: recentAvg >= olderAvg ? 'up' : 'down',
        priority: 'medium',
      });
    }
    const avgMood = recentEntries.reduce((s, e) => s + (e.mood || 0), 0) / recentEntries.length;
    insights.push({
      title: 'Mood & Consistency',
      description: `Avg mood: ${avgMood.toFixed(1)}/10 over ${recentEntries.length} entries. ${avgMood >= 7 ? 'Great work!' : 'Consider what might boost your mood.'}`,
      trend: avgMood >= 7 ? 'up' : 'stable',
      priority: 'medium',
    });
    return insights;
  };

  const avgStats = entries.length > 0
    ? {
        mood: (entries.reduce((s, e) => s + (e.mood || 0), 0) / entries.length).toFixed(1),
        energy: (entries.reduce((s, e) => s + (e.energy || 0), 0) / entries.length).toFixed(1),
        sleep: (entries.reduce((s, e) => s + (e.sleep || 0), 0) / entries.length).toFixed(1),
      }
    : { mood: '--', energy: '--', sleep: '--' };

  const insights = computeInsights();

  const RatingSlider = ({ label, icon: Icon, value, setValue, color }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon size={13} className={color} />
          <span className="text-[12px] text-zinc-300">{label}</span>
        </div>
        <span className="text-[14px]">{label === 'Mood' ? moodEmojis[value - 1] : `${value}/10`}</span>
      </div>
      <input
        type="range"
        min="1"
        max="10"
        value={value}
        onChange={(e) => setValue(parseInt(e.target.value))}
        className="w-full h-[3px] bg-[#1A1A1A] rounded-full appearance-none cursor-pointer accent-amber-400"
      />
    </div>
  );

  return (
    <div className="min-h-full bg-black text-white p-3 pb-16">

      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <BookOpen size={14} className="text-amber-400" />
        <div>
          <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600">Wellness</p>
          <h1 className="text-[15px] font-semibold tracking-tight">Smart Journal</h1>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-200 mb-3">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="grid grid-cols-3 gap-1 rounded-xl border border-[#1F1F1F] bg-[#0A0A0A] p-1 mb-3">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] transition-all ${
                activeTab === tab.id
                  ? 'bg-amber-400 text-black font-semibold'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <Icon size={11} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {loading && activeTab !== 'calendar' ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={20} className="text-amber-400 animate-spin" />
        </div>
      ) : (
        <>
          {activeTab === 'calendar' && (
            <div className="space-y-3">
              {/* Date Nav */}
              <div className="flex items-center justify-between rounded-xl border border-[#1F1F1F] bg-[#0A0A0A] px-3 py-2">
                <button onClick={() => changeDate(-1)} className="p-1">
                  <ChevronLeft size={14} className="text-zinc-400" />
                </button>
                <span className="text-[12px] text-zinc-200">{formatDisplayDate(selectedDate)}</span>
                <button onClick={() => changeDate(1)} className="p-1">
                  <ChevronRight size={14} className="text-zinc-400" />
                </button>
              </div>

              {/* Check-in Form */}
              <div className="rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A] px-4 py-3 space-y-4">
                <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600">
                  {todayEntry ? 'Update Check-in' : "Today's Check-in"}
                </p>
                <RatingSlider label="Mood" icon={Smile} value={moodRating} setValue={setMoodRating} color="text-yellow-500" />
                <RatingSlider label="Energy" icon={Zap} value={energyLevel} setValue={setEnergyLevel} color="text-orange-500" />
                <RatingSlider label="Sleep Quality" icon={Moon} value={sleepQuality} setValue={setSleepQuality} color="text-blue-500" />
                <div>
                  <p className="text-[9px] text-zinc-500 mb-1">Notes</p>
                  <textarea
                    value={journalNote}
                    onChange={(e) => setJournalNote(e.target.value)}
                    placeholder="How was your day? Any reflections..."
                    rows={3}
                    className="w-full px-3 py-2 bg-white/[0.03] border border-[#1F1F1F] rounded-xl text-[11px] text-white placeholder-zinc-600 focus:outline-none focus:border-amber-400 resize-none"
                  />
                </div>
                <button
                  onClick={handleSaveEntry}
                  disabled={saving}
                  className="inline-flex w-full items-center justify-center gap-1 rounded-xl border border-amber-400/50 bg-amber-500/15 py-2 text-[11px] font-semibold text-amber-200 disabled:opacity-45"
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {saving ? 'Saving...' : todayEntry ? 'Update Entry' : 'Save Entry'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'entries' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600">
                  Entries {entries.length > 0 && `(${entries.length})`}
                </p>
                <button
                  onClick={() => setActiveTab('calendar')}
                  className="inline-flex items-center gap-1 rounded-full border border-white/20 px-2 py-1 text-[10px] text-zinc-300"
                >
                  <Plus size={10} />
                  New
                </button>
              </div>

              {entries.length === 0 ? (
                <div className="text-center py-10 text-zinc-600">
                  <BookOpen size={28} className="mx-auto mb-2 opacity-30" />
                  <p className="text-[11px]">No entries yet. Start in Check-in.</p>
                </div>
              ) : (
                entries.map((entry, index) => (
                  <button
                    key={entry.id || index}
                    onClick={() => {
                      if (entry.date) { setSelectedDate(new Date(entry.date + 'T12:00:00')); setActiveTab('calendar'); }
                    }}
                    className="w-full text-left rounded-xl border border-[#1F1F1F] bg-[#0A0A0A] px-3 py-2.5"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[12px] text-zinc-100">{formatEntryDate(entry.date || entry.createdAt)}</span>
                      <div className="flex items-center gap-2 text-[11px]">
                        {entry.mood > 0 && <span>😊 {entry.mood}</span>}
                        {entry.energy > 0 && <span>⚡ {entry.energy}</span>}
                        {entry.sleep > 0 && <span>😴 {entry.sleep}</span>}
                      </div>
                    </div>
                    {entry.note && <p className="text-[10px] text-zinc-500 line-clamp-1">{entry.note}</p>}
                  </button>
                ))
              )}
            </div>
          )}

          {activeTab === 'insights' && (
            <div className="space-y-3">
              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { emoji: '😊', value: avgStats.mood, label: 'Avg Mood' },
                  { emoji: '⚡', value: avgStats.energy, label: 'Avg Energy' },
                  { emoji: '😴', value: avgStats.sleep, label: 'Avg Sleep' },
                ].map(({ emoji, value, label }) => (
                  <div key={label} className="rounded-xl border border-[#1F1F1F] bg-[#0A0A0A] px-2 py-3 text-center">
                    <div className="text-[18px] mb-1">{emoji}</div>
                    <div className="text-[16px] font-bold">{value}</div>
                    <div className="text-[9px] text-zinc-500 uppercase tracking-widest">{label}</div>
                  </div>
                ))}
              </div>

              {/* AI Insights */}
              <div className="rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A] px-4 py-3">
                <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 mb-3 flex items-center gap-1.5">
                  <Brain size={10} />
                  AI Insights
                </p>
                {insights.length === 0 ? (
                  <div className="text-center py-4 text-zinc-600">
                    <Brain size={24} className="mx-auto mb-2 opacity-30" />
                    <p className="text-[11px]">Log 2+ entries to unlock insights.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {insights.map((insight, index) => (
                      <div
                        key={index}
                        className={`rounded-xl border px-3 py-2 ${
                          insight.priority === 'high'
                            ? 'border-amber-400/30 bg-amber-400/[0.08]'
                            : 'border-[#1F1F1F] bg-white/[0.02]'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-[12px] text-zinc-100">{insight.title}</span>
                          {insight.trend === 'up' && <TrendingUp size={11} className="text-green-400" />}
                        </div>
                        <p className="text-[10px] text-zinc-500">{insight.description}</p>
                      </div>
                    ))}
                  </div>
                )}
                <p className="mt-3 text-[9px] text-zinc-700">
                  Based on {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
